/**
 * ReviewQueueDrawerView — pure presentation for the pipeline Review-queue
 * drawer (SE-5499, restyled SE-6554).
 *
 * Right-anchored side drawer on desktop, bottom sheet on mobile (one Drawer
 * atom, `anchor` switched by screen size — the project gates layout on screen
 * size, never device type). Hosts the header with its pending-count badge, the
 * type filter, the row list, and the loading / empty / error states.
 *
 * The type filter is only drawn when the queue actually holds more than one
 * kind of request: PRD §7 ships v1 with rule suggestions alone, and a filter
 * row offering a single choice is chrome that cannot do anything.
 * @remarks View component — no business logic, hooks, or state.
 */

import { memo } from "react";

import { TonePill } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import {
  Drawer,
  DrawerBackdrop,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from "@/components/atoms/Drawer";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { XIcon } from "lucide-react-native";

import {
  PIPELINE_DRAWER_CONTENT_CLASS,
  PIPELINE_DRAWER_SHEET_CLASS,
} from "../../../constants";

import ReviewQueueRow from "../../molecules/ReviewQueueRow";

import type {
  ReviewQueueRow as ReviewQueueRowModel,
  ReviewQueueSourceFilter,
} from "../../../utils/reviewQueueRows";

/** One source-filter option descriptor. */
export interface ReviewQueueFilterOption {
  readonly key: ReviewQueueSourceFilter;
  /** SE-6554: the label already carries its count ("Scout flags · 1"). */
  readonly label: string;
}

/** Props for {@link ReviewQueueDrawerView}. */
export interface ReviewQueueDrawerViewProps {
  /** Whether the drawer is open. */
  readonly isOpen: boolean;
  /** Close handler. */
  readonly onClose: () => void;
  /** Whether the small-screen (bottom-sheet) layout is active. */
  readonly isMobile: boolean;
  /** Rows to render (already filtered + sorted). */
  readonly rows: readonly ReviewQueueRowModel[];
  /** Whether the queue is loading. */
  readonly loading: boolean;
  /** Whether the queue errored. */
  readonly isError: boolean;
  /**
   * SE-6554: rows still awaiting a decision, across the whole queue — the
   * header badge. 0 renders no badge rather than a "0" nobody needs to see.
   */
  readonly pendingCount: number;
  /** Filter options — empty renders no filter row at all. */
  readonly filterOptions: readonly ReviewQueueFilterOption[];
  /** Active source filter. */
  readonly sourceFilter: ReviewQueueSourceFilter;
  /** Pre-built per-option press handlers keyed by filter (avoids inline fns). */
  readonly filterHandlers: Readonly<
    Record<ReviewQueueSourceFilter, () => void>
  >;
  /** Ids with an in-flight decision. */
  readonly decidingIds: ReadonlySet<string>;
  /** Accept handler. */
  readonly onAccept: (row: ReviewQueueRowModel) => void;
  /** Decline handler. */
  readonly onDecline: (row: ReviewQueueRowModel) => void;
}

/**
 * Render the queue body: spinner, error, empty, or the row list.
 * @param props - Body inputs.
 * @returns The body content node.
 */
const renderBody = (
  props: Pick<
    ReviewQueueDrawerViewProps,
    "rows" | "loading" | "isError" | "decidingIds" | "onAccept" | "onDecline"
  >
) => {
  if (props.loading && props.rows.length === 0) {
    return (
      <Box className="items-center justify-center py-10">
        <Spinner />
      </Box>
    );
  }
  if (props.isError) {
    return (
      <Box className="items-center justify-center py-10">
        <Text variant="body" className="text-status-error">
          Could not load the Review queue. Please try again.
        </Text>
      </Box>
    );
  }
  if (props.rows.length === 0) {
    return (
      <Box
        testID="review-queue-empty"
        className="items-center justify-center py-10"
      >
        <Text variant="body" className="text-content-secondary">
          No review requests.
        </Text>
      </Box>
    );
  }
  return (
    <Stack className="gap-2">
      {props.rows.map(row => (
        <ReviewQueueRow
          key={row.id}
          row={row}
          isDeciding={props.decidingIds.has(row.id)}
          onAccept={props.onAccept}
          onDecline={props.onDecline}
        />
      ))}
    </Stack>
  );
};

/**
 * Render the type-filter row, or nothing when there is nothing to choose
 * between.
 *
 * A render helper rather than a component so the directory keeps its
 * index/Container/View trio.
 * @param props - Filter inputs.
 * @returns The filter row node, or null.
 */
const renderFilters = (
  props: Pick<
    ReviewQueueDrawerViewProps,
    "filterOptions" | "sourceFilter" | "filterHandlers"
  >
) => {
  if (props.filterOptions.length === 0) return null;
  return (
    <Stack
      direction="horizontal"
      className="flex-wrap items-center gap-2 px-5 py-3"
    >
      {props.filterOptions.map(option => (
        <Pressable
          key={option.key}
          onPress={props.filterHandlers[option.key]}
          accessibilityLabel={`Filter: ${option.label}`}
          testID={`review-queue-filter-${option.key}`}
        >
          {/* The pill's chrome — radius, fill, label colour — is the atom's
              closed tone pairing; the Pressable adds only the hit area. */}
          <TonePill
            tone={
              option.key === props.sourceFilter ? "accent" : "neutral-raised"
            }
            label={option.label}
            textVariant="micro"
            className="px-3 py-1"
          />
        </Pressable>
      ))}
    </Stack>
  );
};

/**
 * Render the Review-queue drawer.
 * @param props - {@link ReviewQueueDrawerViewProps}.
 * @returns The drawer.
 */
const ReviewQueueDrawerView = (props: ReviewQueueDrawerViewProps) => (
  <Drawer
    isOpen={props.isOpen}
    onClose={props.onClose}
    anchor={props.isMobile ? "bottom" : "right"}
    size={props.isMobile ? "lg" : "md"}
  >
    <DrawerBackdrop />
    <DrawerContent
      testID="review-queue-drawer"
      className={
        props.isMobile
          ? PIPELINE_DRAWER_SHEET_CLASS
          : PIPELINE_DRAWER_CONTENT_CLASS
      }
    >
      <DrawerHeader className="border-b border-outline-default px-5 py-4">
        <Stack
          direction="horizontal"
          className="flex-1 items-center justify-between gap-4"
        >
          <Stack direction="horizontal" className="min-w-0 items-center gap-2">
            <Text variant="title-sm" className="text-content-primary">
              Review queue
            </Text>
            {props.pendingCount === 0 ? null : (
              <TonePill
                tone="accent"
                label={String(props.pendingCount)}
                textVariant="micro"
                className="px-2 py-micro"
                testID="review-queue-count"
              />
            )}
          </Stack>
          <Pressable
            onPress={props.onClose}
            accessibilityLabel="Close review queue"
            testID="review-queue-close"
            className="size-8 shrink-0 items-center justify-center rounded-md bg-surface-raised"
          >
            <Icon as={XIcon} size="sm" className="text-content-secondary" />
          </Pressable>
        </Stack>
      </DrawerHeader>

      <DrawerBody className="flex-1 p-0">
        {renderFilters(props)}
        <ScrollView showsVerticalScrollIndicator={false}>
          <Stack className="px-5 pb-6 pt-1">
            {renderBody({
              rows: props.rows,
              loading: props.loading,
              isError: props.isError,
              decidingIds: props.decidingIds,
              onAccept: props.onAccept,
              onDecline: props.onDecline,
            })}
          </Stack>
        </ScrollView>
      </DrawerBody>
    </DrawerContent>
  </Drawer>
);

ReviewQueueDrawerView.displayName = "ReviewQueueDrawerView";

export default memo(ReviewQueueDrawerView);
